import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import Icon from "../../components/AppIcon";
import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import Sidebar from "../../components/ui/Sidebar";
import Button from "../../components/ui/Button";

import { useCreateDevice } from "./useCreateDevice";
import { useGetBranches } from "./useGetBranches";
import { useGetDevices } from "./useGetDevices";

import toast from "react-hot-toast";
import { useGetCompany } from "pages/company-settings/useGetCompany";

import DeviceOverviewTab from "./components/DeviceOverviewTab";
import ProductionCertificatesTab from "./components/ProductionCertificatesTab";
import ComplianceCertificatesTab from "./components/ComplianceCertificatesTab";
import CreateDeviceModal from "./components/CreateDeviceModal";

const DeviceSettingsContent = () => {
  const { t } = useTranslation("pages");
  const {
    data: devices,
    isLoading: isLoadingDevices,
    error: devicesError,
  } = useGetDevices();

  const { data: branches = [] } = useGetBranches();
  const { data: company } = useGetCompany();
  const { isCollapsed } = useSidebar();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");

  // Use createDevice hook
  const {
    mutate: createDevice,
    isLoading: isCreatingDevice,
    error: createDeviceError,
  } = useCreateDevice();
  // console.log(devices);

  //   // Mock device data - replace with actual API call
  //   const [devices, setDevices] = useState([
  //     {
  //       id: "1",
  //       deviceSerialNumber: "00005644",

  //       vatNumber: "300000000000003",
  //       organizationName: "Riyadh Electronics",
  //       organizationUnitName: "RE",
  //       organizationIdentifier: "300000000000003",
  //       invoiceType: "0100",
  //       location: "22 Street B block",
  //       industry: "Electronics",
  //       countryCode: "SA",
  //       csr: {
  //         requestId: "122200002",
  //       },
  //       complianceStatus: "active",
  //       productionStatus: "pending",
  //       createdAt: "2024-01-15",
  //       lastUsed: "2024-02-20",
  //     },
  //     {
  //       id: "2",
  //       deviceSerialNumber: "00005645",

  //       vatNumber: "300000000000003",
  //       organizationName: "Riyadh Electronics",
  //       organizationUnitName: "RE",
  //       organizationIdentifier: "300000000000003",
  //       invoiceType: "0200",
  //       location: "25 Street C block",
  //       industry: "Electronics",
  //       countryCode: "SA",
  //       csr: {
  //         requestId: "122200003",
  //       },
  //       complianceStatus: "pending",
  //       productionStatus: "inactive",
  //       createdAt: "2024-02-01",
  //       lastUsed: "2024-02-18",
  //     },
  //   ]);

  const tabs = [
    {
      id: "overview",
      label: t("deviceSettings.tabs.overview.label"),
      icon: "Monitor",
      description: t("deviceSettings.tabs.overview.description"),
    },
    {
      id: "compliance",
      label: t("deviceSettings.tabs.compliance.label"),
      icon: "Shield",
      description: t("deviceSettings.tabs.compliance.description"),
    },
    {
      id: "production",
      label: t("deviceSettings.tabs.production.label"),
      icon: "Award",
      description: t("deviceSettings.tabs.production.description"),
    },
  ];

  const filteredDevices = Array.isArray(devices)
    ? devices?.filter(
        (device) =>
          device.deviceSerialNumber
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          device.organizationName
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          device.location.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : [];

  const handleCreateDevice = (data) => {
    createDevice(data, {
      onSuccess: (response) => {
        console.log("Device created successfully:", response);
        setIsCreateModalOpen(false);
      },
      onError: (error) => {
        const isAuthError = error?.response?.message?.includes("logged in");
        if (isAuthError) {
          toast.error(t("sidebar:profile.authRequired", { defaultValue: "You must be logged in to access this resource" }), {
            id: "auth-error",
          });
        }
        toast.error(error.message);
        console.error("Failed to create device:", error);
        // Optionally show error message
        // toast.error("Failed to register device. Please try again.");
      },
    });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <DeviceOverviewTab devices={filteredDevices} company={company} />
        );
      case "compliance":
        return <ComplianceCertificatesTab devices={devices} />;
      case "production":
        return <ProductionCertificatesTab devices={devices} />;
      default:
        return (
          <DeviceOverviewTab devices={filteredDevices} company={company} />
        );
    }
  };

  // Loading state
  if (isLoadingDevices) {
    return (
      <div
        className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${
          isCollapsed ? "md:ms-16" : "md:ms-64"
        }`}
      >
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 shadow-hero-cta border border-gray-100 dark:border-gray-700">
            <div className="animate-pulse">
              <div className="h-8 bg-muted rounded w-1/4 mb-4"></div>
              <div className="h-4 bg-muted rounded w-1/2 mb-8"></div>
              <div className="flex gap-4 mb-8">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-10 bg-muted rounded w-32"></div>
                ))}
              </div>
              <div className="h-96 bg-muted rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (devicesError) {
    return (
      <div
        className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${
          isCollapsed ? "md:ms-16" : "md:ms-64"
        }`}
      >
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 shadow-hero-cta border border-gray-100 dark:border-gray-700">
            <div className="text-center py-12">
              <Icon
                name="AlertCircle"
                size={48}
                className="text-destructive mx-auto mb-4"
              />
              <h2 className="text-xl font-semibold text-foreground mb-2">
                {t("deviceSettings.failedLoad")}
              </h2>
              <p className="text-muted-foreground mb-4">
                {devicesError.response?.data?.message ||
                  devicesError.message ||
                  t("deviceSettings.unableFetch")}
              </p>
              <Button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                {t("deviceSettings.tryAgain")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${
        isCollapsed ? "md:ms-16" : "md:ms-64"
      }`}
    >
      <Helmet>
        <title>{`${t("deviceSettings.title")} - ZATCA Compliance Management`}</title>
        <meta
          name="description"
          content={t("deviceSettings.metaDescription")}
        />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 shadow-hero-cta border border-gray-100 dark:border-gray-700 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                {t("deviceSettings.title")}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t("deviceSettings.description")}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="relative">
                <Icon
                  name="Search"
                  size={18}
                  className="absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="text"
                  placeholder={t("deviceSettings.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="ps-10 pe-4 py-2 bg-muted border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full sm:w-64"
                />
              </div>

              {/* Create Device Button */}
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                className="bg-primary hover:bg-primary/90 text-primary-foreground flex items-center gap-2"
                disabled={!company?.legalName}
              >
                <Icon name="Plus" size={16} />
                <span>{t("deviceSettings.addDevice")}</span>
              </Button>
            </div>
          </div>

          {/* Desktop Tabs */}
          <div className="flex items-center gap-1 mt-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-micro hover-lift ${
                  activeTab === tab.id
                    ? "bg-primary text-primary-foreground shadow-hero-cta"
                    : "text-muted-foreground hover:text-foreground hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Tab Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 shadow-hero-cta border border-gray-100 dark:border-gray-700"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderTabContent()}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Create Device Modal */}
      <CreateDeviceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateDevice}
        isLoading={isCreatingDevice}
        error={createDeviceError}
        company={company}
        branches={branches}
      />
    </div>
  );
};

const DeviceSettings = () => {
  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        <DeviceSettingsContent />
      </div>
    </SidebarProvider>
  );
};

export default DeviceSettings;
